import { useState, useCallback } from 'react'
import { Drawer, Group, Text, Button, Box, Skeleton, Stack, ScrollArea, Alert } from '@mantine/core'
import { modals } from '@mantine/modals'
import { useMantineTheme, useMediaQuery } from 'hooks'
import { notifications } from 'context'
import { IconCheck, IconX } from '@tabler/icons-react'
import { useArcTranslation } from 'hooks'
import { StudentRequestRow } from './StudentRequestRow'
import { RequestsTableHeader } from './RequestsTableHeader'
import { AddGroupModal } from './AddGroupModal'
import type { useStudentRequests } from './useStudentRequests'

type HookReturn = ReturnType<typeof useStudentRequests>

interface Props {
  opened: boolean
  onClose: () => void
  hook: HookReturn
}

function showSuccess(message: string) {
  notifications.show({ message, color: 'green', icon: <IconCheck size={18} /> })
}

function showError(message: string) {
  notifications.show({ message, color: 'red', icon: <IconX size={18} /> })
}

/** Extract a human-readable message from axios errors or generic Error objects */
function extractErrorMessage(err: unknown): string {
  if (!err || typeof err !== 'object') return ''
  // Axios errors carry the backend message in response.data
  const axiosErr = err as { response?: { data?: unknown } }
  if (axiosErr.response?.data) {
    const data = axiosErr.response.data
    if (typeof data === 'string') return data
    if (typeof data === 'object' && data !== null) {
      const obj = data as Record<string, unknown>
      if (typeof obj.error === 'string') return obj.error
      if (typeof obj.errors === 'string') return obj.errors
      if (Array.isArray(obj.errors)) return obj.errors.join(', ')
    }
  }
  if (err instanceof Error) return err.message
  return ''
}

export function ReviewStudentRequestsModal({ opened, onClose, hook }: Props) {
  const { t } = useArcTranslation()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const [addGroupOpened, setAddGroupOpened] = useState(false)
  const [addGroupLoading, setAddGroupLoading] = useState(false)

  const {
    requests, groups, grades, rowStates, rowLoading, loading, error,
    acceptingAll,
    canFillDownGroup, canFillDownGrade,
    isRowBusy,
    updateRowGroups, updateRowGrade,
    handleAccept, handleDecline, handleAcceptAll,
    validateAll, fillDown, addGroup, fetchData,
  } = hook

  const isDirty = Object.values(rowStates).some(r => r.groupIds.length > 0 || !!r.gradeId)

  const handleClose = useCallback(() => {
    if (isDirty && !acceptingAll) {
      modals.openConfirmModal({
        title: t('general_changed_confirmation_modal_title'),
        children: <Text size='sm'>{t('general_changed_confirmation_modal_message')}</Text>,
        labels: {
          confirm: t('general_changed_confirmation_modal_discard'),
          cancel: t('general_changed_confirmation_modal_cancel'),
        },
        confirmProps: { color: 'red' },
        centered: true,
        onConfirm: onClose,
      })
      return
    }
    onClose()
  }, [isDirty, acceptingAll, onClose, t])

  const handleSingleAccept = useCallback(async (requestId: string) => {
    // Guard against double-click: if row is already busy, do nothing
    if (isRowBusy(requestId)) return

    const req = requests.find(r => r.id === requestId)
    const row = rowStates[requestId]
    const groupNames = groups.filter(g => row?.groupIds.includes(g.id)).map(g => g.name).join(', ')

    try {
      const success = await handleAccept(requestId)
      if (success) {
        showSuccess(t('student_requests.toast_accepted', { name: `${req?.firstName} ${req?.lastName}`, group: groupNames }))
      }
    } catch (err) {
      const detail = extractErrorMessage(err)
      showError(detail ? `${t('student_requests.toast_error')}: ${detail}` : t('student_requests.toast_error'))
    }
  }, [handleAccept, requests, rowStates, groups, isRowBusy, t])

  const handleSingleDecline = useCallback(async (requestId: string) => {
    // Guard against double-click
    if (isRowBusy(requestId)) return

    try {
      await handleDecline(requestId)
      showSuccess(t('student_requests.toast_declined'))
    } catch (err) {
      const detail = extractErrorMessage(err)
      showError(detail ? `${t('student_requests.toast_error')}: ${detail}` : t('student_requests.toast_error'))
    }
  }, [handleDecline, isRowBusy, t])

  const handleAcceptAllClick = useCallback(async () => {
    if (acceptingAll) return

    if (!validateAll()) {
      notifications.show({
        title: t('student_requests.toast_accept_all_error_title'),
        message: t('student_requests.toast_accept_all_error'),
        color: 'red',
        icon: <IconX size={18} />,
      })
      return
    }

    try {
      const groupNames = [...new Set(
        Object.values(rowStates).flatMap(r => r.groupIds)
      )].map(gId => groups.find(g => g.id === gId)?.name).filter(Boolean).join(', ')

      const { accepted, failed } = await handleAcceptAll()

      if (failed > 0) {
        showError(t('student_requests.toast_accepted_all_partial', {
          accepted,
          failed,
          defaultValue: `${accepted} accepted, ${failed} failed. Check remaining rows for details.`,
        }))
      } else if (accepted > 0) {
        showSuccess(t('student_requests.toast_accepted_all', { count: accepted, groups: groupNames }))
        onClose()
      }
    } catch (err) {
      const detail = extractErrorMessage(err)
      showError(detail ? `${t('student_requests.toast_error')}: ${detail}` : t('student_requests.toast_error'))
    }
  }, [acceptingAll, rowStates, groups, validateAll, handleAcceptAll, onClose, t])

  const handleAddGroup = useCallback(async (name: string): Promise<boolean> => {
    setAddGroupLoading(true)
    try {
      await addGroup(name)
      showSuccess(t('student_requests.toast_group_added'))
      return true
    } catch (err) {
      const detail = extractErrorMessage(err)
      showError(detail ? `${t('student_requests.toast_error')}: ${detail}` : t('student_requests.toast_error'))
      return false
    } finally {
      setAddGroupLoading(false)
    }
  }, [addGroup, t])

  return (
    <>
      <Drawer
        opened={opened}
        onClose={handleClose}
        title={<Text size='xl' weight={700}>{t('student_requests.title')}</Text>}
        position='right'
        size={isMobile ? '100%' : '60%'}
        zIndex={100}
        padding='md'
        closeOnClickOutside={!acceptingAll}
        closeOnEscape={!acceptingAll}
        withOverlay
        styles={(theme) => ({
          header: { borderBottom: `1px solid ${theme.colors.gray[2]}`, paddingBottom: 12 },
          body: { display: 'flex', flexDirection: 'column', height: 'calc(100vh - 80px)', padding: 0 },
          drawer: { display: 'flex', flexDirection: 'column' },
        })}
      >
        <ScrollArea sx={{ flex: 1 }} px='md' pt='sm' pb='xs'>
          {error ? (
            <Alert color='red' variant='light' mb='sm' withCloseButton onClose={() => fetchData()}>
              <Text size='sm'>{error}</Text>
              <Button variant='subtle' color='red' size='xs' compact mt='xs' onClick={() => fetchData()}>
                {t('student_requests.retry', 'Retry')}
              </Button>
            </Alert>
          ) : loading ? (
            <Stack spacing='sm'>
              {[0, 1, 2, 3].map(i => <Skeleton key={i} height={48} radius='sm' />)}
            </Stack>
          ) : (
            <Box sx={theme => ({
              border: `1px solid ${theme.colors.gray[2]}`,
              borderRadius: theme.radius.md,
              overflow: 'hidden',
            })}>
              {requests.length > 0 && (
                <RequestsTableHeader
                  canFillDownGroup={canFillDownGroup}
                  canFillDownGrade={canFillDownGrade}
                  onFillDownGroup={() => fillDown('group')}
                  onFillDownGrade={() => fillDown('grade')}
                />
              )}
              {requests.length === 0 ? (
                <Text color='dimmed' align='center' py='xl'>{t('student_requests.empty')}</Text>
              ) : (
                requests.map((req, index) => (
                  <StudentRequestRow
                    key={req.id}
                    request={req}
                    rowState={rowStates[req.id] || { groupIds: [], gradeId: null, error: {} }}
                    groups={groups}
                    grades={grades}
                    loading={rowLoading[req.id] || null}
                    disabled={acceptingAll}
                    isLast={index === requests.length - 1}
                    onGroupChange={ids => updateRowGroups(req.id, ids)}
                    onGradeChange={id => updateRowGrade(req.id, id)}
                    onAccept={() => handleSingleAccept(req.id)}
                    onDecline={() => handleSingleDecline(req.id)}
                    onCreateGroup={() => setAddGroupOpened(true)}
                  />
                ))
              )}
            </Box>
          )}
        </ScrollArea>

        {requests.length > 0 && (
          <Group position='right' px='md' py='md' sx={theme => ({ borderTop: `1px solid ${theme.colors.gray[2]}` })}>
            <Button variant='default' onClick={handleClose} disabled={acceptingAll}>
              {t('student_requests.close')}
            </Button>
            <Button color='teal.8' onClick={handleAcceptAllClick} loading={acceptingAll}>
              {t('student_requests.accept_all')}
            </Button>
          </Group>
        )}
      </Drawer>

      <AddGroupModal
        opened={addGroupOpened}
        onClose={() => setAddGroupOpened(false)}
        onSave={handleAddGroup}
        loading={addGroupLoading}
      />
    </>
  )
}
